// ── Firestore Document Types (MUST match original static app schema exactly) ──

/** Picklist value mapping: maps a CRM picklist option to possible event values */
export interface PicklistMapping {
  saas_value: string;
  tpa_values: string[];
}

/** Field format configuration (stored per field in fields_formats) */
export interface FieldFormatConfig {
  quickFormat?: string;
  filter?: Array<{ type: string; params: Record<string, unknown> }>;
  format?: { type: string; params: Record<string, unknown> };
}

/** Fields for updating existing records (MMM card sub-structure) */
export interface FieldsForExisting {
  fields: Record<string, string>;
  fields_type: Record<string, string>;
  fields_formats?: Record<string, Record<string, FieldFormatConfig>>;
  picklistMapping?: Record<string, PicklistMapping[]>;
  ct: number;
}

/** MMM Card — one module action inside a branch */
export interface MMMCard {
  cardId: string;
  saasFieldModuleName: string;
  displayLabel: string;
  orderOf: number;
  fields: Record<string, string>;
  fields_type: Record<string, string>;
  fields_formats?: Record<string, Record<string, FieldFormatConfig>>;
  picklistMapping?: Record<string, PicklistMapping[]>;
  fieldsForExisting?: FieldsForExisting;
  advanced_link_parent?: string[];
  advanced_link_child?: string[];
  /** Lookup field ID for the parent-child link (returned by getAdvanceLinkFieldIdForMMM) */
  advanced_link_FieldId?: string;
  /** false = always create a new record, skip checking for existing records (same key as legacy) */
  syncToExisting?: boolean;
  /** Old React-only key, never read by the backend — replaced by syncToExisting */
  alwaysCreateNew?: boolean;
  /** Temporarily disable the update-existing field mapping */
  updateDisabled?: boolean;
  ct: number;
}

/** Custom variable (reusable data variable) */
export interface CustomVariable {
  id: string;
  name: string;
  /** Variable value — can contain field references like ${{{$.path}}} */
  value: string;
  /** Advanced formatting/transformation configuration */
  fields_formats?: Record<string, Record<string, FieldFormatConfig>>;
  /** Ordering timestamp */
  orderOf: number;
}

/**
 * Complete Firestore field mapping document.
 * Path: ulgebraUsers/{uid}/extensions/{extensionName}/fieldMappings/{docId}
 *
 * This MUST match the schema used by the original static app exactly.
 */
export interface FieldMappingDocument {
  /** Firestore document ID — set after first save */
  id?: string;
  /** User-given name for this mapping */
  name: string;
  /**
   * When to apply this mapping.
   * Values: "---default", "message-channelId=<id>", "message-platform=<platform>",
   *         "direction-in-to=<number>", "eventtype=<id>", or custom "field=value"
   */
  filterValue: string;
  /** Human-readable label for filterValue */
  filterValueLabel: string;
  /** Always "TPA" */
  fieldMappingType: 'TPA';
  /** Creation timestamp (ms) */
  ct: number;

  // ── Non-MMM (legacy single module) ──
  saasFieldModuleName?: string;
  /** In non-MMM: Record<fieldApiName, mappedValue>. In MMM: Record<cardId, MMMCard> */
  fields?: Record<string, string> | Record<string, MMMCard>;
  fields_type?: Record<string, string>;

  // ── MMM mode ──
  isMMM?: boolean;
  /** Custom reusable variables */
  variables?: Record<string, CustomVariable>;

  // ── Canvas layout ──
  /** Cached flow canvas node positions (saved with branch) */
  nodePositions?: Record<string, { x: number; y: number }>;
}

// ── Quick Format Options ──

export const QUICK_FORMAT_OPTIONS = [
  { value: 'parse_text', label: 'String Format', icon: 'text_fields' },
  { value: 'parse_int', label: 'Number Format', icon: '123' },
  { value: 'parse_json', label: 'JSON Format', icon: 'data_object' },
  { value: 'parse_array', label: 'Array Format (using ",")', icon: 'data_array' },
  { value: 'advanced', label: 'Advanced Formation', icon: 'layers' },
] as const;

// ── Filter Condition Types (for advanced field formatting) ──

export const FILTER_CONDITION_TYPES = {
  exists: { display_label: 'Not Empty', api_name: 'exists', data_type: 'string' },
  not_exists: { display_label: 'Empty', api_name: 'not_exists', data_type: 'string' },
  any_of: { display_label: '(Text) Value is one of', api_name: 'any_of', data_type: 'string' },
  contains: { display_label: '(Text) Contains', api_name: 'contains', data_type: 'string' },
  not_contains: { display_label: '(Text) Not Contains', api_name: 'not_contains', data_type: 'string' },
  equals: { display_label: '(Text) Equals', api_name: 'equals', data_type: 'string' },
  not_equals: { display_label: '(Text) Not Equals', api_name: 'not_equals', data_type: 'string' },
  starts_with: { display_label: '(Text) Starts With', api_name: 'starts_with', data_type: 'string' },
  not_starts_with: { display_label: '(Text) Not Starts With', api_name: 'not_starts_with', data_type: 'string' },
  ends_with: { display_label: '(Text) Ends With', api_name: 'ends_with', data_type: 'string' },
  not_ends_with: { display_label: '(Text) Not Ends With', api_name: 'not_ends_with', data_type: 'string' },
  greater_than: { display_label: '(Number) Greater than', api_name: 'greater_than', data_type: 'number' },
  less_than: { display_label: '(Number) Less than', api_name: 'less_than', data_type: 'number' },
  is_true: { display_label: '(Boolean) Is true', api_name: 'is_true', data_type: 'boolean' },
  is_false: { display_label: '(Boolean) Is false', api_name: 'is_false', data_type: 'boolean' },
} as const;

// ── Format Transformation Types ──

export const FORMAT_CONDITION_TYPES = {
  capitalize: { display_label: 'Capitalize', api_name: 'capitalize' },
  default: { display_label: 'Default Value', api_name: 'default' },
  extract_email: { display_label: 'Extract Email Address', api_name: 'extract_email' },
  extract_number: { display_label: 'Extract Number', api_name: 'extract_number' },
  extract_phone_number: { display_label: 'Extract Phone Number', api_name: 'extract_phone_number' },
  extract_url: { display_label: 'Extract URL', api_name: 'extract_url' },
  length: { display_label: 'Length', api_name: 'length' },
  lowercase: { display_label: 'LowerCase', api_name: 'lowercase' },
  titlecase: { display_label: 'TitleCase', api_name: 'titlecase' },
  trim: { display_label: 'Trim', api_name: 'trim' },
  trim_all: { display_label: 'Trim All WhiteSpace', api_name: 'trim_all' },
  url_encode: { display_label: 'URL Encode', api_name: 'url_encode' },
  url_decode: { display_label: 'URL Decode', api_name: 'url_decode' },
  word_count: { display_label: 'Words Count', api_name: 'word_count' },
  parse_int: { display_label: 'Parse Number Format', api_name: 'parse_int' },
  parse_text: { display_label: 'Parse Text Format', api_name: 'parse_text' },
  replace: { display_label: 'Replace Text', api_name: 'replace' },
  replace_value: { display_label: 'Change New Value', api_name: 'replace_value' },
  split: { display_label: 'Split', api_name: 'split' },
  slice: { display_label: 'Slice', api_name: 'slice' },
  substr: { display_label: 'Sub String', api_name: 'substr' },
  prefix: { display_label: 'Add Prefix Text', api_name: 'prefix' },
  postfix: { display_label: 'Add Postfix Text', api_name: 'postfix' },
  math: { display_label: 'Math Operations', api_name: 'math' },
  dateFormats: { display_label: 'Date Formats', api_name: 'dateFormats' },
  timeFormats: { display_label: 'Time Formats', api_name: 'timeFormats' },
  dateTimeFormats: { display_label: 'Date Time Formats', api_name: 'dateTimeFormats' },
} as const;

// ── MMM Supporting SaaS list ──

export const MMM_ALLOWING_SAAS = [
  'monday', 'pipedrive', 'zohocrm', 'hubspotcrm', 'freshworkscrm',
  'bitrix24', 'zohodesk', 'freshdesk', 'zendesksupport', 'high',
] as const;

export const MMM_DISALLOWING_DIRECT_UPDATE = ['monday'] as const;

// ── Flow Builder UI Types ──

/** Flow node types for React Flow */
export type FlowNodeType = 'trigger' | 'condition' | 'action' | 'actionCard' | 'end' | 'addBranch' | 'defaultAction';

/** Data carried by the trigger node */
export interface TriggerNodeData {
  type: 'trigger';
  label: string;
  appType: 'messaging' | 'events';
}

/** Data carried by a condition node */
export interface ConditionNodeData {
  type: 'condition';
  /** Firestore doc ID (null = unsaved new branch) */
  docId: string | null;
  filterValue: string;
  filterValueLabel: string;
  isDefault: boolean;
  /** Whether this branch has action cards configured */
  hasActions: boolean;
}

/** Data carried by an action node (container — kept for drop target on empty branches) */
export interface ActionNodeData {
  type: 'action';
  docId: string | null;
  cards: MMMCard[];
  isMMM: boolean;
  /** Whether the SaaS app supports adding multiple module cards per branch (UI-only) */
  isMultiModule: boolean;
  name: string;
  /** Only sync existing records — skip creating new ones */
  ignoreNewCustomers: boolean;
  /** Only sync new records — skip existing ones */
  ignoreExistingCustomers: boolean;
}

/** Data carried by an individual action card node (one per MMM card) */
export interface ActionCardNodeData {
  type: 'actionCard';
  docId: string;
  card: MMMCard;
  cardIndex: number;
  totalCards: number;
}

/** Data carried by an end node */
export interface EndNodeData {
  type: 'end';
  docId?: string | null;
}

/** Data carried by the add-branch node */
export interface AddBranchNodeData {
  type: 'addBranch';
}

/** Data carried by the default-action node (fallback when no condition matches) */
export interface DefaultActionNodeData {
  type: 'defaultAction';
  /** Firestore doc ID of the ---default branch */
  docId: string | null;
  /** Action cards from the default branch (if configured) */
  cards: MMMCard[];
  isMMM: boolean;
  isMultiModule: boolean;
  name: string;
  /**
   * Target module from incoming capture settings (e.g., 'Leads' or a board ID).
   * null when the SaaS has no default module (e.g., Monday boards) and the user
   * hasn't selected one yet — the node then prompts to pick a module.
   */
  targetModule: string | null;
  /** Display label for the target module (resolved from moduleLabelMap, e.g., board name for Monday) */
  targetModuleLabel: string | null;
  /** Whether the default branch has configured cards with fields */
  hasConfiguredCards: boolean;
  /** Only sync existing records — skip creating new ones */
  ignoreNewCustomers: boolean;
  /** Only sync new records — skip existing ones */
  ignoreExistingCustomers: boolean;
}

/** Union of all node data types */
export type FlowNodeData =
  | TriggerNodeData
  | ConditionNodeData
  | ActionNodeData
  | ActionCardNodeData
  | EndNodeData
  | AddBranchNodeData
  | DefaultActionNodeData;

/** Recent event from Firestore activity log */
export interface RecentEvent {
  id: string;
  title: string;
  ct: number;
  actualSourceObj: Record<string, unknown>;
  eventTypeValue?: string;
  eventTypeName?: string;
}

/** Sidebar section types */
export type SidebarSection = 'modules' | 'fields' | 'variables';

/** Parsed event field path with sample value */
export interface EventFieldPath {
  path: string;
  sampleValue: unknown;
  /** Display label when the leaf key alone is ambiguous (e.g. "Address - Answer") */
  label?: string;
}

/** Messaging app condition types */
export const MESSAGING_CONDITION_TYPES = [
  { value: 'message-channelId=', label: 'Channel ID', prefix: 'message-channelId=' },
  { value: 'message-platform=', label: 'Platform', prefix: 'message-platform=' },
  { value: 'direction-in-to=', label: 'Incoming To Number', prefix: 'direction-in-to=' },
] as const;
